import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import TrackCard from "../components/TrackCard";
import { Globe2, TrendingUp, MapPin, Play } from "lucide-react";

const COUNTRIES = [
  { name: "Madagascar", flag: "🇲🇬", color: "from-[#E31B23] via-white to-[#0B8B47]" },
  { name: "Sénégal", flag: "🇸🇳", color: "from-[#00853F] via-[#FDEF42] to-[#E31B23]" },
  { name: "Nigeria", flag: "🇳🇬", color: "from-[#008751] via-white to-[#008751]" },
  { name: "Côte d'Ivoire", flag: "🇨🇮", color: "from-[#FF8200] via-white to-[#009E60]" },
  { name: "Cameroun", flag: "🇨🇲", color: "from-[#007A5E] via-[#CE1126] to-[#FCD116]" },
  { name: "RDC", flag: "🇨🇩", color: "from-[#007FFF] via-[#F7D618] to-[#CE1021]" },
  { name: "Mali", flag: "🇲🇱", color: "from-[#14B53A] via-[#FCD116] to-[#CE1126]" },
  { name: "Afrique du Sud", flag: "🇿🇦", color: "from-[#007A4D] via-[#FFB612] to-[#DE3831]" },
  { name: "Kenya", flag: "🇰🇪", color: "from-black via-[#BB0000] to-[#006600]" },
  { name: "Ghana", flag: "🇬🇭", color: "from-[#CE1126] via-[#FCD116] to-[#006B3F]" },
  { name: "Maroc", flag: "🇲🇦", color: "from-[#C1272D] via-[#C1272D] to-[#006233]" },
  { name: "Tunisie", flag: "🇹🇳", color: "from-[#E70013] via-white to-[#E70013]" },
];

export const TrendingCountryPage = () => {
  const [selected, setSelected] = useState("Madagascar");
  const [tracks, setTracks] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/tracks", { params: { country: selected, sort: "trending", limit: 24 } })
      .then(({ data }) => setTracks(data))
      .finally(() => setLoading(false));
  }, [selected]);

  useEffect(() => {
    // Fetch counts per country in parallel
    Promise.all(
      COUNTRIES.map((c) =>
        api.get("/tracks", { params: { country: c.name, limit: 1 } })
          .then(({ data }) => [c.name, data.length > 0])
          .catch(() => [c.name, false])
      )
    ).then((pairs) => {
      // For a richer view, fetch actual counts (up to 50)
      Promise.all(
        COUNTRIES.map((c) =>
          api.get("/tracks", { params: { country: c.name, limit: 50 } })
            .then(({ data }) => [c.name, data.length])
            .catch(() => [c.name, 0])
        )
      ).then((arr) => {
        const map = Object.fromEntries(arr);
        setCounts(map);
      });
    });
  }, []);

  return (
    <div className="px-6 md:px-12 py-10 max-w-7xl mx-auto pb-32" data-testid="trending-country-page">
      <p className="inline-flex items-center gap-2 tag-pill mb-5 bg-brand-green/15 border-brand-green/40 text-brand-green">
        <Globe2 size={12} /> Classement par pays
      </p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-4 text-balance">
        Le top du <span className="bg-gradient-to-r from-[#E31B23] via-white to-[#0B8B47] bg-clip-text text-transparent">continent</span>,
        <br />pays par pays.
      </h1>
      <p className="text-white/70 max-w-2xl text-lg mb-10">
        Découvrez ce qui fait vibrer Antananarivo, Dakar, Lagos, Abidjan… Le meilleur du son local, en direct.
      </p>

      {/* Country pills */}
      <div className="flex flex-wrap gap-2 mb-10" data-testid="country-selector">
        {COUNTRIES.map((c) => (
          <button
            key={c.name}
            onClick={() => setSelected(c.name)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 border text-sm font-medium transition-all ${
              selected === c.name
                ? "border-brand-red bg-brand-red/10 text-brand-red"
                : "border-white/10 bg-white/[0.02] text-white/70 hover:bg-white/[0.06]"
            }`}
            data-testid={`country-btn-${c.name}`}
          >
            <span className="text-base leading-none">{c.flag}</span>
            {c.name}
            {counts[c.name] !== undefined && (
              <span className="text-xs text-white/40 font-mono">{counts[c.name]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Featured country banner */}
      {(() => {
        const country = COUNTRIES.find((c) => c.name === selected);
        return (
          <div className={`relative rounded-2xl overflow-hidden border border-white/10 mb-10 p-8 md:p-10 bg-gradient-to-r ${country?.color} bg-opacity-20`} data-testid="country-hero">
            <div className="absolute inset-0 bg-brand-dark/60" />
            <div className="relative">
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/70 font-bold mb-2 inline-flex items-center gap-2">
                <TrendingUp size={12} /> Top tendance
              </p>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter flex items-center gap-4">
                <span className="text-5xl md:text-6xl">{country?.flag}</span>
                {selected}
              </h2>
              <p className="text-white/80 mt-3">
                {tracks.length} morceau{tracks.length > 1 ? "x" : ""} en vedette ·
                {" "}
                {tracks.reduce((s, t) => s + (t.plays || 0), 0)} écoutes cumulées
              </p>
            </div>
          </div>
        );
      })()}

      {/* Tracks */}
      {loading ? (
        <p className="text-white/50">Chargement...</p>
      ) : tracks.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center">
          <MapPin size={36} className="mx-auto text-white/40 mb-4" />
          <h3 className="font-display text-2xl font-bold mb-2">Pas encore de morceau depuis {selected}</h3>
          <p className="text-white/60 mb-6">Vous êtes artiste de ce pays ? Soyez le premier à poser votre empreinte.</p>
          <Link to="/upload" className="btn-primary inline-flex items-center gap-2">
            <Play size={16} fill="currentColor" /> Publier le premier morceau
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 stagger">
          {tracks.map((t, i) => (
            <div key={t.track_id} className="relative">
              {i < 3 && (
                <span className="absolute -top-2 -left-2 z-10 w-8 h-8 rounded-full bg-brand-red text-white font-display font-black text-sm flex items-center justify-center shadow-lg">
                  #{i + 1}
                </span>
              )}
              <TrackCard track={t} list={tracks} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrendingCountryPage;
